<template>
  <el-card style="min-height: 100vh;">
    <el-form
      ref="formRef"
      :rules="rules"
      :model="form" label-width="100px">
      <el-form-item label="活动类型">
        <el-select
          :disabled="!isEdit"
          v-model="form.type">
          <el-option label="单品折扣" value="single" />
        </el-select>
      </el-form-item>
      <el-form-item
        prop="title"
        label="活动标题">
        <el-input
          :disabled="!isEdit"
          v-model="form.title" />
      </el-form-item>
      <el-form-item
        prop="time"
        label="活动时间">
        <el-date-picker
          v-model="form.time"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          :disabled-date="disabledDate"
          :disabled="!isEdit"
        />
      </el-form-item>

      <el-form-item label="活动商品" prop="products">
        <el-button type="primary"
                   :disabled="!isEdit"
                   @click="dialogVisible = true">选择商品</el-button>

      </el-form-item>
      <el-table
        ref="productTableRef"
        id="productTable"
        :data="form.products"
        row-key="spuCode"
        border
        style="margin: 12px 0 32px;margin-left: 100px;width: 60vw;"
      >
        <el-table-column  width="70" align="center">
          <template #header>
           <div>排序</div>
          </template>
          <template #default>
            <el-icon class="drag-handle" style="cursor: grab; font-size: 18px; color: #909399"><Rank /></el-icon>
          </template>
        </el-table-column>
        <el-table-column prop="spuCode" label="商品SPU" />
        <el-table-column prop="spuName" label="商品名称" />
        <el-table-column prop="stockNum" label="库存" />
        <el-table-column prop="stockNum"
                         :formatter="(row)=>{
                           if(row.minPrice){
                             if(row.minPrice!==row.maxPrice){
                               return '¥'+row.minPrice+'-'+'¥'+row.maxPrice
                             }
                             return '¥'+row.minPrice
                           }
                           if(row.minSellingPrice!==row.maxSellingPrice){
                             return '¥'+(row.minSellingPrice)+'-'+'¥'+(row.maxSellingPrice)
                           }
                           return '¥'+row.minSellingPrice
                         }"
                         label="价格" />
        <el-table-column label="促销折扣(%)">
          <template #default="{ row }">
            <el-input
              v-model="row.discount"
              placeholder="1-100"
              type="number"
              :min="1"
              :max="100"
              @input="row.discount = normalizeInt(row.discount, 1, 100)"
            />
          </template>
        </el-table-column>

        <el-table-column label="总限量">
          <template #default="{ row }">
            <el-input
              v-model="row.totalLimit"
              placeholder="整数"
              type="number"
              :min="1"
              @input="row.totalLimit = normalizeInt(row.totalLimit, 1)"
            />
          </template>
        </el-table-column>

        <el-table-column label="每人限量">
          <template #default="{ row }">
            <el-input
              v-model="row.perPersonLimit"
              placeholder="整数"
              type="number"
              :min="1"
              @input="row.perPersonLimit = normalizeInt(row.perPersonLimit, 1)"
            />
          </template>
        </el-table-column>
        <el-table-column label="剩余活动库存">
          <template #default="{ row }">
            <el-input
              v-model="row.activityStock"
              placeholder="整数"
              type="number"
              :min="1"
              disabled
            />
          </template>
        </el-table-column>

        <!-- ✅ 修改：操作列宽度由 180 改为 80，删除上移下移按钮 -->
        <el-table-column
          label="操作"
          width="80"
          align="center"
        >
          <template #default="{ $index }">
            <el-button
              type="danger"
              link
              :disabled="!isEdit"
              @click="removeProduct($index)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-form-item>
        <div class="form-footer">
          <el-button @click="closeTab">取消</el-button>
          <el-button
            v-if="isEdit"
            type="primary" @click="publishAct">发布</el-button>
        </div>
      </el-form-item>
    </el-form>
  </el-card>

  <ProductDialog v-model:visible="dialogVisible" :selected-codes="form.products.map(p => p.spuCode)" @confirm="addProducts" />
</template>

<script setup lang="ts">
import { ref, reactive, nextTick, watch } from 'vue'
import {dateUtil, onMounted, useCurrentAppInstance} from '@zto/zpage'
const app = useCurrentAppInstance()
import ProductDialog from './ProductDialog.vue'
import {ElMessage} from "element-plus";
import {dayjs} from "@zto/zpage-runtime/utils/date";
import { ElMessageBox } from 'element-plus'
import type { FormInstance, FormRules } from 'element-plus'
import Sortable from 'sortablejs'
import { Rank } from '@element-plus/icons-vue'

const formRef = ref<FormInstance>()
const productTableRef = ref()
const dialogVisible = ref(false)
const isEdit = ref(false)
const form = reactive({
  type: 'single',
  title: '',
  time: [],
  products: [] as any[],
  activityCode: '' // 补充定义，防止ts报错
})

const normalizeInt = (
  value: any,
  min = 1,
  max?: number
) => {
  if (value === '' || value === null || value === undefined) return ''

  let num = parseInt(value, 10)

  if (isNaN(num)) return ''

  if (num < min) num = min
  if (max !== undefined && num > max) num = max

  return num
}

// ✅ 新增：初始化拖拽排序
const sortableInstance = ref<any>(null)
const initSort = () => {
  nextTick(() => {
    const tableEl = productTableRef.value?.$el
    if (!tableEl) return
    const el = tableEl.querySelector('.el-table__body-wrapper tbody') || tableEl.querySelector('tbody')
    if (!el) return

    // 如果已经存在实例，先销毁，防止重复绑定
    if (sortableInstance.value) {
      sortableInstance.value.destroy()
    }

    sortableInstance.value = Sortable.create(el as HTMLElement, {
      handle: '.drag-handle',
      animation: 150,
      draggable: '.el-table__row',
      onEnd: ({ oldIndex, newIndex }: any) => {
        if (oldIndex === newIndex) return
        const currRow = form.products.splice(oldIndex, 1)[0]
        form.products.splice(newIndex, 0, currRow)
      }
    })
  })
}

const removeProduct = (index: number) => {
  ElMessageBox.confirm('确认删除该商品吗？', '提示', {
    confirmButtonText:'确认',
    cancelButtonText: '取消',
    type: 'warning' })
    .then(() => {
      form.products.splice(index, 1)
      formRef.value?.clearValidate()
      ElMessage.success('已删除')
    })
}

function getHashQuery() {
  const hash = window.location.hash || ''
  const queryStr = hash.includes('?') ? hash.split('?')[1] : ''
  return new URLSearchParams(queryStr)
}
const rules: FormRules = {
  title: [
    { required: true, message: '请输入活动标题', trigger: 'blur' }
  ],
  time: [
    { required: true, message: '请选择活动时间', trigger: 'change' }
  ],
  products: [
    {
      validator: (_rule, value, callback) => {
        if (!value || value.length === 0) {
          callback(new Error('请至少选择一个活动商品'))
        } else {
          callback()
        }
      },
      trigger: 'change'
    }
  ]
}

async function queryData(){
  const query = getHashQuery()
  const id = query.get('id')
  const activityCode = query.get('activityCode')
  if (activityCode){
    isEdit.value = query.get('type')==='edit'
    const resp = await app.request({
      api:'admin/activity/queryInfo',
      params: {
        activityCode: activityCode,
      }
    })
    form.title = resp.activityTitle
    form.time = [
      dateUtil.parseAndFormat(resp.startDate),
      dateUtil.parseAndFormat(resp.endDate)
    ]
    form.activityCode = activityCode
    form.type = resp.activityTypeName

    const newGoodsList = resp.goodsList.sort((a: any, b: any) => {
      return a.sort - b.sort
    })
    form.products = newGoodsList.map((item: any)=>{
      item.stockNum = item.stock
      return item;
    })
    initSort()
  }
  else {
    isEdit.value = true
    initSort()
  }
}

onMounted(()=>{
  queryData()
})

// 监听编辑状态变化，重新初始化拖拽
watch(isEdit, (val) => {
  if (val) initSort()
})

// 监听商品列表长度变化（比如删除或新增），确保拖拽实例仍然有效
watch(() => form.products.length, () => {
  if (isEdit.value) initSort()
})

const disabledDate = (time: Date) => {
  return time.getTime() < dayjs().startOf('day').valueOf()
}

const addProducts = (rows: any[]) => {
  // 已存在的 spuCode
  const existSet = new Set(
    form.products.map(item => item.spuCode)
  )

  const newList: any[] = []
  const repeatList: string[] = []

  rows.forEach(item => {
    if (existSet.has(item.spuCode)) {
      repeatList.push(item.spuName || item.spuCode)
    } else {
      existSet.add(item.spuCode)
      newList.push(item)
    }
  })

  if (newList.length) {
    form.products.push(...newList)
    initSort()
  }

  if (repeatList.length) {
    // ElMessage.warning(
    //   `以下商品已存在，已自动忽略：${repeatList.join('、')}`
    // )
  }
}

const closeTab=()=>{
  app.router.close()
}

const publishAct = async () => {
  if (!formRef.value) return

  await formRef.value.validate(async (valid) => {
    if (!valid) return
    const myStartDate = dateUtil.parseAndFormat(form.time[0])

    await app.request({
      api:'admin/activity/saveOrUpdate',
      params: {
        activityType: 1,
        activityTitle: form.title,
        startDate: myStartDate,
        endDate: dateUtil.parseAndFormat(form.time[1]),
        goodsList: form.products.map((item,index)=>{
          item.sort = index
          return item;
        }),
        activityCode: form.activityCode ? form.activityCode : undefined
      }
    })
    ElMessage.success('创建成功')
    setTimeout(() => {
      app.router.close()
    }, 1000)
  })
}

</script>

<style scoped>
.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

/* 拖拽相关样式 */
.drag-handle:hover {
  color: #409eff !important;
  cursor: grab;
}
.drag-handle:active {
  cursor: grabbing;
}
</style>
